import * as React from 'react';
import { Edit, SimpleForm, Toolbar, useNotify, useRedirect, useRecordContext } from 'react-admin';
import { Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { UserRole, IProductRecycle } from '../types/types';
import useAuthorize from '../hooks/useAuthorize';
import { RecycleProductFormFields, submitRecycleProduct, postRecycleAction } from './RecycleProductFormFields';
import { useRecycleSellerContext } from './useRecycleSellerContext';

const EditToolbarInner = ({ isSeller }: { isSeller: boolean }) => {
    const redirect = useRedirect();
    const notify = useNotify();
    const { getValues } = useFormContext();
    const record = useRecordContext<IProductRecycle>();
    const { sellerId } = useRecycleSellerContext();

    const canSellerEdit = isSeller && record && ['draft', 'rejected'].includes(record.status || '');

    const saveListing = async (statusAction?: string) => {
        try {
            const values = getValues();
            const response = await submitRecycleProduct(
                'PUT',
                `/productrecycle/${values.id}`,
                values,
                isSeller ? sellerId : null,
                isSeller
                    ? { statusAction: statusAction || 'draft' }
                    : { status: values.status }
            );
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Failed to update reprocessed product');
            }
            notify('resources.productrecycle.notification.updated', {
                type: 'info',
                messageArgs: { smart_count: 1, name: 'Reprocessed product' },
            });
            redirect('/productrecycle');
        } catch (error: any) {
            notify(error.message || 'Error updating reprocessed product', { type: 'error' });
        }
    };

    const runAction = async (path: string, body?: Record<string, unknown>) => {
        try {
            const response = await postRecycleAction(path, body);
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || 'Action failed');
            }
            notify('Listing updated', { type: 'success' });
            redirect('/productrecycle');
        } catch (error: any) {
            notify(error.message || 'Action failed', { type: 'error' });
        }
    };

    return (
        <Toolbar>
            {isSeller && canSellerEdit && (
                <>
                    <Button variant="contained" onClick={() => saveListing('draft')}>Save Draft</Button>
                    <Button variant="contained" color="secondary" onClick={() => saveListing('submit')} sx={{ ml: 1 }}>
                        Submit for Approval
                    </Button>
                </>
            )}
            {!isSeller && (
                <Button variant="contained" onClick={() => saveListing()}>Save</Button>
            )}
            {!isSeller && record?.status === 'submitted' && (
                <>
                    <Button color="success" onClick={() => runAction(`/productrecycle/${record.id}/approve`)} sx={{ ml: 1 }}>
                        Approve
                    </Button>
                </>
            )}
            <Button color="primary" onClick={() => redirect('/productrecycle')} style={{ marginLeft: 10 }}>
                Back
            </Button>
        </Toolbar>
    );
};

export const ProductRecycleEdit = (props: any) => {
    const { isSeller } = useRecycleSellerContext();
    useAuthorize([UserRole.Admin, UserRole.Seller]);

    return (
        <Edit {...props}>
            <SimpleForm toolbar={<EditToolbarInner isSeller={isSeller} />}>
                <RecycleProductFormFields showAdminStatus={!isSeller} />
            </SimpleForm>
        </Edit>
    );
};

export default ProductRecycleEdit;
